import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate, useParams, Navigate, useLocation } from 'react-router-dom';
import './App.css';
import Header from './components/Header';
import TabNavigation from './components/TabNavigation';
import SummariesTab from './components/SummariesTab';
import ModelReactions from './components/ModelReactions';
import NotificationsTab from './components/NotificationsTab';
import UsersTab from './components/UsersTab';
import UserDetailPage from './components/UserDetailPage';
import FinanceTab from './components/FinanceTab';
import ProductMetricsTab from './components/ProductMetricsTab';
import AddHoursModal from './components/AddHoursModal';
import PaymentsModal from './components/PaymentsModal';
import LoginPage from './components/LoginPage';
import ProtectedRoute from './components/ProtectedRoute';
import { AuthProvider, useAuth } from './context/AuthContext';
import { DataProvider, useData } from './context/DataContext';
import { API } from './api';

function App() {
  return (
    <AuthProvider>
      <DataProvider>
        <Router>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/" element={<Navigate to="/tab/summaries" replace />} />
            <Route path="/tab/:tabName" element={<ProtectedRoute><AppContent /></ProtectedRoute>} />
            <Route path="/user/:userId" element={<ProtectedRoute><AppContent /></ProtectedRoute>} />
          </Routes>
        </Router>
      </DataProvider>
    </AuthProvider>
  );
}

function AppContent() {
  const navigate = useNavigate();
  const location = useLocation();
  const { tabName, userId } = useParams();
  const { users, usersPagination, stats, audioDuration, loading, updateUser, fetchUsers } = useData();
  
  const [activeTab, setActiveTab] = useState(tabName || 'summaries');
  const [selectedUserId, setSelectedUserId] = useState(userId ? parseInt(userId) : null);
  const [showAddHoursModal, setShowAddHoursModal] = useState(false);
  const [showPaymentsModal, setShowPaymentsModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const { logout } = useAuth();

  // Update active tab from URL
  useEffect(() => {
    setActiveTab(tabName || 'summaries');
  }, [tabName]);

  useEffect(() => {
    setSelectedUserId(userId ? parseInt(userId, 10) : null);
  }, [userId]);

  // Update URL when tab or user changes
  useEffect(() => {
    if (selectedUserId) {
      const nextPath = `/user/${selectedUserId}`;
      if (location.pathname !== nextPath) {
        navigate(nextPath);
      }
    } else if (activeTab) {
      const nextPath = `/tab/${activeTab}`;
      if (location.pathname !== nextPath) {
        navigate(nextPath);
      }
    }
  }, [selectedUserId, activeTab, navigate, location.pathname]);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setSelectedUserId(null);
  };

  const handleAddHours = async (hours, reason) => {
    try {
      const result = await API.users.addHours(selectedUser.id, hours, reason);
      if (result.success) {
        // Update user in local state (which comes from context)
        // We need to update the context state
        const updatedUser = { ...selectedUser, hours: result.data.newTotal };
        updateUser(updatedUser);
        
        // Also update selectedUser if it's the one open in modal
        if (selectedUser.id === updatedUser.id) {
           setSelectedUser(updatedUser);
        }
        
        setShowAddHoursModal(false);
      }
    } catch (error) {
      console.error('Error adding hours:', error);
    }
  };

  const openAddHoursModal = (user) => {
    setSelectedUser(user);
    setShowAddHoursModal(true);
  };

  const openPaymentsModal = (user) => {
    setSelectedUser(user);
    setShowPaymentsModal(true);
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
      </div>
    );
  }

  // Render detail view if a user is selected
  if (selectedUserId) {
    return (
      <div className="app-container">
        <Header 
          onLogout={logout}
          showLogout={true}
        />
        <UserDetailPage
          userId={selectedUserId}
          onBack={() => setSelectedUserId(null)}
          onAddHours={() => {
            const user = users.find(u => u.id === selectedUserId);
            if (user) openAddHoursModal(user);
          }}
          onViewPayments={() => {
            const user = users.find(u => u.id === selectedUserId);
            if (user) openPaymentsModal(user);
          }}
        />
        {showAddHoursModal && (
          <AddHoursModal
            user={selectedUser}
            onClose={() => setShowAddHoursModal(false)}
            onSave={handleAddHours}
          />
        )}
        {showPaymentsModal && (
          <PaymentsModal
            user={selectedUser}
            onClose={() => setShowPaymentsModal(false)}
            currency="USD"
          />
        )}
      </div>
    );
  }

  return (
    <div className="app-container">
      <Header 
        onLogout={logout}
        showLogout={true}
      />
      <main className="main-content">
        <div className="content-wrapper">
          <TabNavigation activeTab={activeTab} onTabChange={handleTabChange} />

          <div className="container">
            {activeTab === 'summaries' && (
              <SummariesTab 
                stats={stats}
                audioDuration={audioDuration}
                loading={loading} 
                onUserClick={setSelectedUserId}
              />
            )}

            {activeTab === 'product' && (
              <ProductMetricsTab />
            )}

            {activeTab === 'finance' && (
              <FinanceTab onUserClick={setSelectedUserId} />
            )}

            {activeTab === 'models' && (
              <ModelReactions />
            )}

            {activeTab === 'users' && (
              <UsersTab
                users={users}
                pagination={usersPagination}
                stats={stats}
                loading={loading}
                onUserClick={setSelectedUserId}
                onAddHours={openAddHoursModal}
                onViewPayments={openPaymentsModal}
                onPageChange={fetchUsers}
              />
            )}

            {activeTab === 'notifications' && (
              <NotificationsTab />
            )}
          </div>
        </div>
      </main>

      {showAddHoursModal && (
        <AddHoursModal
          user={selectedUser}
          onClose={() => setShowAddHoursModal(false)}
          onSave={handleAddHours}
        />
      )}

      {showPaymentsModal && (
        <PaymentsModal
          user={selectedUser}
          onClose={() => setShowPaymentsModal(false)}
          currency="USD"
        />
      )}
    </div>
  );
}

export default App;
